import React, { useState } from 'react';
import { ArrowLeft, User, CheckCircle, XCircle, Eye } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import Avatar from '../../../components/ui/avatar';
import { useClientData } from './hooks/useClientData';
import { useClientImages } from './hooks/useClientImages';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, getAllUsers } from '../../../store/user';
import { startImpersonation } from '../../../store/admin';
import PersonalInfoCard from './profile-cards/PersonalInfoCard';
import AddressCard from './profile-cards/AddressCard';
import BrokerInfoCard from './profile-cards/BrokerInfoCard';
import BrandingCard from './profile-cards/BrandingCard';
import PersonalDetailsCard from './profile-cards/PersonalDetailsCard';
import SocialMediaCard from './profile-cards/SocialMediaCard';
import SchedulingCard from './profile-cards/SchedulingCard';
import SubscriptionCard from './profile-cards/SubscriptionCard';
import ClientImagesPanel from './ClientImagesPanel';
import { STATUS_CONFIG, STATUS_CHANGE_OPTIONS } from '../../../constants/userStatus';

const ClientDetailsEnhanced = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const clientDataHook = useClientData();
	const { client, loading, error, updateField } = clientDataHook;
	const clientImages = useClientImages(client);
	const isUpdating = useSelector((st) => st.user.pending); // Use Redux loading state
	const [updateError, setUpdateError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);

	// Get primary headshot image
	const getPrimaryHeadshot = () => {
		if (!clientImages.getImagesByType) return null;

		const headshotImages = clientImages.getImagesByType('headshot');
		if (!headshotImages || headshotImages.length === 0) return null;

		// Find primary image or use first available
		const primaryImage =
			headshotImages.find((img) => img.isPrimary) || headshotImages[0];
		return primaryImage?.imageUrl || null;
	};

	// Get client initials for fallback
	const getClientInitials = () => {
		if (!client) return '';
		const firstName = client.firstName || '';
		const lastName = client.lastName || '';

		if (!firstName && !lastName) return '';
		if (!lastName) return firstName.charAt(0).toUpperCase();
		if (!firstName) return lastName.charAt(0).toUpperCase();

		return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
	};

	if (loading) {
		return (
			<div className='min-h-screen bg-stone-50 flex items-center justify-center'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-primary mx-auto mb-4'></div>
					<p className='text-sm font-montserrat-regular text-gray-400'>Loading client details...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
				<Card className='w-full max-w-md'>
					<Card.Content className='text-center py-8'>
						<div className='text-red-500 mb-4'>⚠️</div>
						<h3 className='text-lg font-semibold text-gray-900 mb-2'>
							Error Loading Client
						</h3>
						<p className='text-gray-500 mb-4'>{error}</p>
						<Button
							onClick={() => history.push('/admin/clients')}
							variant='outline'>
							<ArrowLeft className='w-4 h-4 mr-2' />
							Back to Clients
						</Button>
					</Card.Content>
				</Card>
			</div>
		);
	}

	if (!client) {
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
				<Card className='w-full max-w-md'>
					<Card.Content className='text-center py-8'>
						<User className='w-12 h-12 text-gray-400 mx-auto mb-4' />
						<h3 className='text-lg font-semibold text-gray-900 mb-2'>
							Client Not Found
						</h3>
						<p className='text-gray-500 mb-4'>
							The requested client could not be found.
						</p>
						<Button
							onClick={() => history.push('/admin/clients')}
							variant='outline'>
							<ArrowLeft className='w-4 h-4 mr-2' />
							Back to Clients
						</Button>
					</Card.Content>
				</Card>
			</div>
		);
	}

	const getOnboardingBadge = () => {
		return client.onboardingCompleted ? (
			<Badge variant='default' className='bg-green-100 text-green-800'>
				Complete
			</Badge>
		) : (
			<Badge variant='secondary' className='bg-yellow-100 text-yellow-800'>
				Incomplete
			</Badge>
		);
	};

	// Helper function to set message timeout based on message length
	const setMessageWithTimeout = (message, setter) => {
		setter(message);
		const timeout = Math.max(5000, message.length * 50);
		setTimeout(() => setter(null), timeout);
	};

	// Handle onboarding toggle
	const handleOnboardingToggle = async () => {
		setUpdateError(null);
		setSuccessMessage(null);
		
		// Token validation
		const token = clientDataHook.token;
		if (!token) {
			setUpdateError('Authentication required. Please log in again.');
			return;
		}
		
		// Data type validation
		if (typeof client.onboardingCompleted !== 'boolean') {
			console.warn('Invalid onboarding status type:', typeof client.onboardingCompleted);
			setUpdateError('Invalid client data. Please refresh the page.');
			return;
		}
		
		const newOnboardingStatus = !client.onboardingCompleted;
		
		try {
			// Batch all updates into a single object
			const updates = {
				onboarding_completed: newOnboardingStatus
			};

			// Only include profile_completion_date if we're marking as complete
			if (newOnboardingStatus) {
				updates.profile_completion_date = new Date().toISOString();
			}

			// Use Redux updateUser action for consistency
			await dispatch(updateUser({
				username: client.username,
				data: updates,
				token
			})).unwrap();

			// Refresh users list to update UI state
			dispatch(getAllUsers({ token, status: 'all' }));

			// Show success message
			const message = newOnboardingStatus
				? 'Client onboarding marked as complete'
				: 'Client onboarding marked as incomplete';
			setMessageWithTimeout(message, setSuccessMessage);
		} catch (error) {
			console.error('Error updating onboarding status:', error);
			const errorMessage = 'Failed to update onboarding status. Please try again.';
			setMessageWithTimeout(errorMessage, setUpdateError);
		}
	};

	// Handle verification toggle
	const handleVerificationToggle = async () => {
		setUpdateError(null);
		setSuccessMessage(null);

		// Token validation
		const token = clientDataHook.token;
		if (!token) {
			setUpdateError('Authentication required. Please log in again.');
			return;
		}

		// Data type validation
		if (typeof client.verified !== 'boolean') {
			console.warn('Invalid verification status type:', typeof client.verified);
			setUpdateError('Invalid client data. Please refresh the page.');
			return;
		}

		const newVerificationStatus = !client.verified;

		try {
			// Update verification status
			const updates = {
				verified: newVerificationStatus
			};

			// Use Redux updateUser action for consistency
			await dispatch(updateUser({
				username: client.username,
				data: updates,
				token
			})).unwrap();

			// Refresh users list to update UI state
			dispatch(getAllUsers({ token, status: 'all' }));

			// Show success message
			const message = newVerificationStatus
				? 'Client email marked as verified'
				: 'Client email marked as unverified';
			setMessageWithTimeout(message, setSuccessMessage);
		} catch (error) {
			console.error('Error updating verification status:', error);
			const errorMessage = 'Failed to update verification status. Please try again.';
			setMessageWithTimeout(errorMessage, setUpdateError);
		}
	};

	// Handle account status change
	const handleStatusChange = async (e) => {
		const newStatus = e.target.value;
		setUpdateError(null);
		setSuccessMessage(null);

		const token = clientDataHook.token;
		if (!token) {
			setUpdateError('Authentication required. Please log in again.');
			return;
		}

		if (newStatus === 'deleted') {
			if (
				!window.confirm(
					`Are you sure you want to mark ${client.firstName} ${client.lastName} as deleted? This will prevent them from logging in.`
				)
			) {
				return;
			}
		}

		try {
			await dispatch(
				updateUser({
					username: client.username,
					data: { profile_status: newStatus },
					token,
				})
			).unwrap();

			// Refresh users list to update UI state
			dispatch(getAllUsers({ token, status: 'all' }));

			clientDataHook.setClient((prev) => ({
				...prev,
				profileStatus: newStatus,
				profile_status: newStatus,
			}));

			const statusLabel =
				STATUS_CONFIG[newStatus]?.label || newStatus;
			const message = `Client status changed to ${statusLabel}`;
			setMessageWithTimeout(message, setSuccessMessage);
		} catch (error) {
			console.error('Error updating status:', error);
			const errorMessage =
				'Failed to update account status. Please try again.';
			setMessageWithTimeout(errorMessage, setUpdateError);
		}
	};

	const primaryHeadshot = getPrimaryHeadshot();
	const clientInitials = getClientInitials();

	// Handle impersonation
	const handleImpersonate = async () => {
		try {
			await dispatch(startImpersonation(client.username)).unwrap();
			// Navigate to user home page
			history.push('/home');
		} catch (error) {
			console.error('Impersonation failed:', error);
			setUpdateError('Failed to start impersonation. Please try again.');
		}
	};

	return (
		<div className='min-h-screen bg-stone-50'>
			{/* Header — Tier 1: Client Identity */}
			<div className='bg-white border-b border-gray-100'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='flex items-center py-4'>
						<Button
							variant='ghost'
							size='sm'
							onClick={() => history.push('/admin/clients')}
							className='text-gray-400 hover:text-gray-700 transition-colors'>
							<ArrowLeft className='w-4 h-4 mr-2' />
							Back
						</Button>
						<div className='h-6 border-l border-gray-200 mx-4 hidden sm:block'></div>
						<div className='flex items-center space-x-4 ml-2 sm:ml-0'>
							<Avatar className='h-14 w-14 shadow-sm border-2 border-gray-100'>
								{primaryHeadshot ? (
									<Avatar.Image
										src={primaryHeadshot}
										alt={`${client.firstName} ${client.lastName}`}
									/>
								) : (
									<Avatar.Fallback className='bg-stone-100 text-gray-700 text-base font-glacial uppercase'>
										{clientInitials}
									</Avatar.Fallback>
								)}
							</Avatar>
							<div>
								<h1 className='text-2xl font-glacial uppercase tracking-wide text-gray-900'>
									{client.firstName} {client.lastName}
								</h1>
								<p className='text-sm font-montserrat-regular text-gray-400 mt-0.5'>
									@{client.username}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Header — Tier 2: Admin Controls */}
			<div className='bg-gray-50/80 border-b border-gray-100'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='flex flex-wrap items-center gap-3 lg:gap-4 py-3'>
						{/* View as User Button */}
						<Button
							variant='outline'
							size='sm'
							onClick={handleImpersonate}
							className='text-primary border-primary/50 hover:bg-primary/10 hover:border-primary transition-colors'>
							<Eye className='w-4 h-4 mr-2' />
							View as User
						</Button>

						{/* Vertical Divider — hidden on mobile */}
						<div className='h-8 border-l border-gray-200 hidden sm:block'></div>

						{/* Email Verification Toggle */}
						<div className='flex items-center gap-2 flex-shrink-0'>
							<label className='text-xs sm:text-sm font-montserrat-medium text-gray-600 cursor-pointer whitespace-nowrap' htmlFor='verification-toggle'>
								Verified
							</label>
							<Badge
								variant={client.verified ? 'default' : 'secondary'}
								className={`hidden sm:inline-flex ${client.verified ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
								{client.verified ? (
									<><CheckCircle className='w-3 h-3 mr-1 inline' />Yes</>
								) : (
									<><XCircle className='w-3 h-3 mr-1 inline' />No</>
								)}
							</Badge>
							<button
								id='verification-toggle'
								type='button'
								role='switch'
								aria-checked={client.verified}
								aria-label={client.verified ? 'Email verified — toggle to mark unverified' : 'Email unverified — toggle to mark verified'}
								onClick={handleVerificationToggle}
								disabled={isUpdating}
								className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
									client.verified ? 'bg-green-600' : 'bg-gray-300'
								}`}>
								<span
									className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
										client.verified ? 'translate-x-6' : 'translate-x-1'
									}`}
								/>
							</button>
						</div>

						{/* Vertical Divider — hidden on mobile */}
						<div className='h-8 border-l border-gray-200 hidden sm:block'></div>

						{/* Onboarding Status Toggle */}
						<div className='flex items-center gap-2 flex-shrink-0'>
							<label className='text-xs sm:text-sm font-montserrat-medium text-gray-600 cursor-pointer whitespace-nowrap' htmlFor='onboarding-toggle'>
								Onboarding
							</label>
							<Badge
								variant={client.onboardingCompleted ? 'default' : 'secondary'}
								className={`hidden sm:inline-flex ${client.onboardingCompleted ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}`}>
								{client.onboardingCompleted ? (
									<><CheckCircle className='w-3 h-3 mr-1 inline' />Complete</>
								) : (
									<><XCircle className='w-3 h-3 mr-1 inline' />Incomplete</>
								)}
							</Badge>
							<button
								id='onboarding-toggle'
								type='button'
								role='switch'
								aria-checked={client.onboardingCompleted}
								aria-label={client.onboardingCompleted ? 'Onboarding complete — toggle to mark incomplete' : 'Onboarding incomplete — toggle to mark complete'}
								onClick={handleOnboardingToggle}
								disabled={isUpdating}
								className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
									client.onboardingCompleted ? 'bg-green-600' : 'bg-gray-300'
								}`}>
								<span
									className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
										client.onboardingCompleted ? 'translate-x-6' : 'translate-x-1'
									}`}
								/>
							</button>
						</div>

						{/* Vertical Divider — hidden on mobile */}
						<div className='h-8 border-l border-gray-200 hidden sm:block'></div>

						{/* Account Status Control */}
						<div className='flex items-center gap-2 flex-shrink-0'>
							<label
								className='text-xs sm:text-sm font-montserrat-medium text-gray-600 whitespace-nowrap'
								htmlFor='status-select'>
								Status
							</label>
							<select
								id='status-select'
								value={client.profileStatus || 'active'}
								onChange={handleStatusChange}
								disabled={isUpdating}
								className='px-2 py-1 border border-gray-200 rounded-md text-sm font-montserrat-medium bg-white focus:ring-2 focus:ring-primary/30 focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed'
								aria-label='Change account status'>
								{STATUS_CHANGE_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>
			</div>

			{/* Success/Error Messages */}
			{(successMessage || updateError) && (
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					{successMessage && (
						<div id='onboarding-success' className='mt-4 p-4 bg-green-50 border border-green-200 rounded-md flex items-center' role='status' aria-live='polite'>
							<CheckCircle className='w-5 h-5 text-green-600 mr-2' />
							<span className='text-sm text-green-800'>{successMessage}</span>
						</div>
					)}
					{updateError && (
						<div id='onboarding-error' className='mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center' role='alert' aria-live='assertive'>
							<XCircle className='w-5 h-5 text-red-600 mr-2' />
							<span className='text-sm text-red-800'>{updateError}</span>
						</div>
					)}
				</div>
			)}

			{/* Content */}
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
				<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
					{/* Main Content - Profile Cards */}
					<div className='lg:col-span-2 space-y-6'>
						<PersonalInfoCard client={client} clientDataHook={clientDataHook} />
						<AddressCard client={client} clientDataHook={clientDataHook} />
						<BrokerInfoCard client={client} clientDataHook={clientDataHook} />
						<BrandingCard client={client} clientDataHook={clientDataHook} />
						<PersonalDetailsCard
							client={client}
							clientDataHook={clientDataHook}
						/>
						<SocialMediaCard client={client} clientDataHook={clientDataHook} />
						<SchedulingCard client={client} clientDataHook={clientDataHook} />
					</div>

					{/* Sidebar - Images & Subscriptions */}
					<div className='lg:col-span-1 space-y-6'>
						<ClientImagesPanel client={client} clientImages={clientImages} />
						<SubscriptionCard client={client} clientDataHook={clientDataHook} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ClientDetailsEnhanced;
